import { useEffect, useRef } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import Layout from './components/Layout'
import Dashboard from './pages/Dashboard'
import Login from './pages/Login'
import GoogleCallback from './pages/GoogleCallback'
import LineCallback from './pages/LineCallback'
import ProjectBoard from './pages/ProjectBoard'
import ProjectSettings from './pages/ProjectSettings'
import ProjectTimeline from './pages/ProjectTimeline'
import ProjectFormsPage from './pages/ProjectFormsPage'
import ProjectFormInstanceDetail from './pages/ProjectFormInstanceDetail'
import ProfileSettings from './pages/ProfileSettings'
import AdminPanel from './pages/AdminPanel'
import SystemSettings from './pages/SystemSettings'
import ProjectManagement from './pages/ProjectManagement'
import IssueDetail from './pages/IssueDetail'
import ArchivedIssues from './pages/ArchivedIssues'
import TrashIssues from './pages/TrashIssues'
import SearchPage from './pages/SearchPage'
import AttachmentsPage from './pages/AttachmentsPage'
import WorkLogManagement from './pages/WorkLogManagement'
import WorkLogDetail from './pages/WorkLogDetail'
import GlobalWorkLogs from './pages/GlobalWorkLogs'
import GlobalWorkLogEditor from './pages/GlobalWorkLogEditor'
import GlobalWorkLogDetail from './pages/GlobalWorkLogDetail'
import GlobalWorkLogExport from './pages/GlobalWorkLogExport'
import FormTemplateDesignerPage from './pages/FormTemplateDesigner'
import CalendarSchedulerPage from './pages/CalendarScheduler'
import PrivacyPolicy from './pages/PrivacyPolicy'
import QueueManagement from './pages/QueueManagement'
import FinanceDashboard from './pages/FinanceDashboard'
import FinanceShortcut from './pages/FinanceShortcut'
import {
  ContactList,
  UnitList,
  ParkingSpaceList,
  BillManagement,
  BankReconciliation,
  FinanceSettings,
  VendorList,
} from './pages/finance'
import ProjectLayout from './components/ProjectLayout'
import ProtectedRoute from './components/ProtectedRoute'
import ChatWidget from './components/chat/ChatWidget'
import { useAppSelector, useAppDispatch } from './hooks/redux'
import { getCurrentUser } from './store/slices/authSlice'
import socketService from './services/socket'
import { ToastProvider } from './components/ToastContainer'
import { SystemSettingsProvider } from './hooks/useSystemSettings'
import { useFormNotifications } from './hooks/useFormNotifications'

function App() {
  const { isAuthenticated, loading, user } = useAppSelector(state => state.auth)
  const dispatch = useAppDispatch()
  const initializedRef = useRef(false)
  useFormNotifications()

  useEffect(() => {
    const token = localStorage.getItem('auth_token')
    
    // 如果沒有 token，清理狀態
    if (!token) {
      if (user || isAuthenticated) {
        localStorage.removeItem('user')
      }
      initializedRef.current = true
      return
    }
    
    // 只在有 token 但沒有使用者資料，且未在載入中，且尚未認證，且尚未初始化時才嘗試獲取使用者資料
    if (token && !user && !isAuthenticated && !loading && !initializedRef.current) {
      initializedRef.current = true
      dispatch(getCurrentUser())
    }
    
    // 如果已有 token 和 user 但未認證，可能是初始化問題，直接設為已初始化
    if (token && user && !isAuthenticated && !loading && !initializedRef.current) {
      initializedRef.current = true
    }
    
    // 如果認證失敗，標記為已初始化
    if (initializedRef.current && !loading && !isAuthenticated && token) {
      initializedRef.current = true
    }
  }, [dispatch, user, isAuthenticated, loading])

  // Socket.IO 連接管理
  useEffect(() => {
    if (isAuthenticated) {
      // 用戶認證成功後連接 Socket
      socketService.connect()
    } else {
      // 用戶未認證時斷開 Socket 連接
      socketService.disconnect()
    }

    // 清理函數
    return () => {
      if (!isAuthenticated) {
        socketService.disconnect()
      }
    }
  }, [isAuthenticated])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <ToastProvider>
      <SystemSettingsProvider>
        <Routes>
      <Route path="/login" element={
        isAuthenticated ? <Navigate to="/" replace /> : <Login />
      } />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/auth/google/callback" element={<GoogleCallback />} />
      <Route path="/auth/line/callback" element={<LineCallback />} />
      <Route path="/" element={
        isAuthenticated ? (
          <Layout>
            <Dashboard />
          </Layout>
        ) : (
          <Navigate to={`/login?redirect=${encodeURIComponent(window.location.pathname)}`} replace />
        )
      } />
      <Route path="/projects" element={
        isAuthenticated ? (
          <Layout>
            <Dashboard />
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      <Route path="/project/:projectKey/board" element={
        isAuthenticated ? (
          <Layout>
            <ProjectLayout>
              <ProjectBoard />
            </ProjectLayout>
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      <Route path="/project/:projectKey/settings" element={
        isAuthenticated ? (
          <Layout>
            <ProjectLayout>
              <ProjectSettings />
            </ProjectLayout>
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      <Route path="/project/:projectKey/timeline" element={
        isAuthenticated ? (
          <Layout>
            <ProjectLayout>
              <ProjectTimeline />
            </ProjectLayout>
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      <Route path="/project/:projectKey/forms" element={
        isAuthenticated ? (
          <Layout>
            <ProjectLayout>
              <ProjectFormsPage />
            </ProjectLayout>
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      <Route path="/project/:projectKey/forms/instances/:instanceId" element={
        isAuthenticated ? (
          <Layout>
            <ProjectLayout>
              <ProjectFormInstanceDetail />
            </ProjectLayout>
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      <Route path="/project/:projectKey/worklogs" element={
        isAuthenticated ? (
          <Layout>
            <ProjectLayout>
              <WorkLogManagement />
            </ProjectLayout>
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      <Route path="/project/:projectKey/worklogs/:workLogId" element={
        isAuthenticated ? (
          <Layout>
            <ProjectLayout>
              <WorkLogDetail />
            </ProjectLayout>
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      <Route path="/profile" element={
        isAuthenticated ? (
          <Layout>
            <ProfileSettings />
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      <Route path="/admin" element={
        isAuthenticated ? (
          <Layout>
            <AdminPanel />
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      <Route path="/admin/queues" element={
        isAuthenticated ? (
          <Layout>
            <QueueManagement />
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      <Route path="/admin/projects" element={
        isAuthenticated ? (
          <Layout>
            <ProjectManagement />
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      <Route path="/project/:projectKey/archived" element={
        isAuthenticated ? (
          <Layout>
            <ProjectLayout>
              <ArchivedIssues />
            </ProjectLayout>
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      <Route path="/project/:projectKey/trash" element={
        isAuthenticated ? (
          <Layout>
            <ProjectLayout>
              <TrashIssues />
            </ProjectLayout>
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      <Route path="/issues/:issueId" element={<IssueDetail />} />
      <Route path="/search" element={
        isAuthenticated ? (
          <Layout>
            <SearchPage />
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      <Route path="/project/:projectKey/attachments" element={
        isAuthenticated ? (
          <Layout>
            <ProjectLayout>
              <AttachmentsPage />
            </ProjectLayout>
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      {/* Finance Module Routes */}
      <Route path="/finance" element={
        isAuthenticated ? (
          <Layout>
            <FinanceShortcut />
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      <Route path="/project/:projectKey/finance" element={
        isAuthenticated ? (
          <Layout>
            <ProjectLayout>
              <FinanceDashboard />
            </ProjectLayout>
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      <Route path="/project/:projectKey/finance/contacts" element={
        isAuthenticated ? (
          <Layout>
            <ProjectLayout>
              <ContactList />
            </ProjectLayout>
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      <Route path="/project/:projectKey/finance/units" element={
        isAuthenticated ? (
          <Layout>
            <ProjectLayout>
              <UnitList />
            </ProjectLayout>
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      <Route path="/project/:projectKey/finance/parking" element={
        isAuthenticated ? (
          <Layout>
            <ProjectLayout>
              <ParkingSpaceList />
            </ProjectLayout>
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      <Route path="/project/:projectKey/finance/bills" element={
        isAuthenticated ? (
          <Layout>
            <ProjectLayout>
              <BillManagement />
            </ProjectLayout>
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      <Route path="/project/:projectKey/finance/reconciliation" element={
        isAuthenticated ? (
          <Layout>
            <ProjectLayout>
              <BankReconciliation />
            </ProjectLayout>
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      <Route path="/project/:projectKey/finance/settings" element={
        isAuthenticated ? (
          <Layout>
            <ProjectLayout>
              <FinanceSettings />
            </ProjectLayout>
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      <Route path="/project/:projectKey/finance/vendors" element={
        isAuthenticated ? (
          <Layout>
            <ProjectLayout>
              <VendorList />
            </ProjectLayout>
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      <Route path="/forms/templates" element={
        <ProtectedRoute>
          <Layout>
            <FormTemplateDesignerPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute>
          <Layout>
            <CalendarSchedulerPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/global-worklogs" element={
        <ProtectedRoute>
          <Layout>
            <GlobalWorkLogs />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/global-worklogs/create" element={
        <ProtectedRoute>
          <Layout>
            <GlobalWorkLogEditor />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/global-worklogs/:id/edit" element={
        <ProtectedRoute>
          <Layout>
            <GlobalWorkLogEditor />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/global-worklogs/:id" element={
        <ProtectedRoute>
          <Layout>
            <GlobalWorkLogDetail />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/global-worklogs/:id/export" element={
        <ProtectedRoute>
          <GlobalWorkLogExport />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        isAuthenticated ? (
          <Layout>
            <SystemSettings />
          </Layout>
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      </Routes>
      
      {/* AI 聊天組件 - 只在已認證用戶時顯示 */}
      {isAuthenticated && <ChatWidget />}
      
      </SystemSettingsProvider>
    </ToastProvider>
  )
}

export default App
